/* Types */
import {
  MenuPageBuilder,
  menuPageBuilderFooterSpacingDefaults,
  sanitizeMenuPageBuilder,
} from 'src/shared/dbTypes/menuPageBuilder'
import type { LandingPageImageAiGenerated } from 'src/functions/landingPageImageAiGenerated'

/* Components */
import TopMarken from './TopMarken'
import InfosUndLinks from './InfosUndLinks'
import Copyright from './Copyright'
import Timestamp from './Timestamp'
import Fussnoten from './Fussnoten'

interface Props {
  pageBuilder?: MenuPageBuilder
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const Footer: React.FC<Props> = ({ pageBuilder, landingPageImageAiGenerated }) => {
  const safeBuilder = sanitizeMenuPageBuilder(pageBuilder)
  const footer = safeBuilder?.footer
  const footerEnabled = Boolean(footer?.enabled)
  const showTimestamp = process.env.NEXT_PUBLIC_TIMESTAMP && (
    footerEnabled
      ? footer?.showTimestamp !== false
      : true
  )

  if (!footerEnabled || !footer) {
    return <footer>
      <TopMarken landingPageImageAiGenerated={landingPageImageAiGenerated} />
      <InfosUndLinks landingPageImageAiGenerated={landingPageImageAiGenerated} />
      {showTimestamp && <Timestamp />}
      <Copyright />
      <Fussnoten />
    </footer>
  }

  const dividerMarginTop = footer.dividerMarginTop ?? menuPageBuilderFooterSpacingDefaults.dividerMarginTop
  const effectiveDividerMarginTop = footer.showDivider
    ? Math.max(32, dividerMarginTop)
    : dividerMarginTop
  const footerStyle = {
    backgroundColor: footer.surfaceColor || footer.backgroundColor,
  }
  const dividerStyle = {
    backgroundColor: footer.surfaceColor || footer.backgroundColor,
    borderBottom: `1px solid ${footer.mutedTextColor || 'rgba(42, 33, 26, 0.16)'}`,
    height: `${effectiveDividerMarginTop}px`,
  }
  const infosPaddingTop = footer.infosPaddingTop ?? menuPageBuilderFooterSpacingDefaults.infosPaddingTop
  const adjustedInfosPaddingTop = footer.showDivider
    ? Math.max(0, infosPaddingTop - effectiveDividerMarginTop)
    : infosPaddingTop
  const infosPaddingBottom = footer.infosPaddingBottom ?? menuPageBuilderFooterSpacingDefaults.infosPaddingBottom
  const legalPaddingTop = footer.legalPaddingTop ?? menuPageBuilderFooterSpacingDefaults.legalPaddingTop
  const legalPaddingBottom = footer.legalPaddingBottom ?? menuPageBuilderFooterSpacingDefaults.legalPaddingBottom
  const footnotesPaddingTop = footer.footnotesPaddingTop ?? menuPageBuilderFooterSpacingDefaults.footnotesPaddingTop
  const footnotesPaddingBottom = footer.footnotesPaddingBottom ?? menuPageBuilderFooterSpacingDefaults.footnotesPaddingBottom

  return <footer style={footerStyle}>
    {footer.showDivider &&
      <div style={dividerStyle} />
    }

    {footer.showTopBrands !== false &&
      <TopMarken
        backgroundColor={footer.brandSectionBackgroundColor || footer.accentColor || footer.backgroundColor}
        textColor={footer.textColor}
        brandBlock={footer.topBrandsBlock}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
    }

    {footer.showInfosLinks !== false &&
      <InfosUndLinks
        backgroundColor={footer.surfaceColor || footer.backgroundColor}
        textColor={footer.textColor}
        columns={footer.columns}
        columnsPerRow={footer.columnsPerRow}
        paddingTop={adjustedInfosPaddingTop}
        paddingBottom={infosPaddingBottom}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
    }

    {showTimestamp &&
      <Timestamp
        backgroundColor={footer.legalBackgroundColor || footer.surfaceColor}
        textColor={footer.mutedTextColor || footer.textColor}
        paddingTop={legalPaddingTop}
        paddingBottom={legalPaddingBottom}
      />
    }

    <Copyright
      text={footer.copyrightText}
      backgroundColor={footer.legalBackgroundColor || footer.surfaceColor}
      textColor={footer.mutedTextColor || footer.textColor}
      paddingTop={legalPaddingTop}
      paddingBottom={legalPaddingBottom}
    />

    {footer.showFootnotes !== false &&
      <Fussnoten
        backgroundColor={footer.backgroundColor}
        textColor={footer.mutedTextColor || footer.textColor}
        linkColor={footer.accentColor}
        primaryHtml={footer.footnotesPrimaryHtml}
        secondaryHtml={footer.footnotesSecondaryHtml}
        paddingTop={footnotesPaddingTop}
        paddingBottom={footnotesPaddingBottom}
      />
    }
  </footer>
}

export default Footer
